// GPU-based Particle System with Object Pooling
// Inspired by MU Online's iconic skill effects with minimal overhead
// Uses THREE.Points for efficient GPU-based rendering

import * as THREE from 'three';

export type ParticleEffectType =
  | 'fire_trail'
  | 'ice_shards'
  | 'holy_light'
  | 'shadow_tendrils'
  | 'lightning_spark'
  | 'arcane_burst'
  | 'blood_splash'
  | 'heal_sparkle'
  | 'level_up'
  | 'loot_sparkle'
  | 'death_soul'
  | 'hit_impact'
  | 'critical_hit'
  | 'buff_aura'
  | 'debuff_aura';

interface ParticleData {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  color: THREE.Color;
  size: number;
  life: number;
  maxLife: number;
  gravity: number;
  fadeOut: boolean;
  shrink: boolean;
  rotation: number;
  rotationSpeed: number;
}

interface ParticlePool {
  particles: ParticleData[];
  activeCount: number;
  geometry: THREE.BufferGeometry;
  points: THREE.Points;
  maxParticles: number;
}

// Effect configurations
const EFFECT_CONFIGS: Record<ParticleEffectType, {
  count: number;
  color: number;
  colorVariance: number;
  size: number;
  sizeVariance: number;
  life: number;
  lifeVariance: number;
  speed: number;
  speedVariance: number;
  gravity: number;
  spread: number;
  fadeOut: boolean;
  shrink: boolean;
  rotationSpeed: number;
}> = {
  fire_trail: {
    count: 20,
    color: 0xff4400,
    colorVariance: 0.3,
    size: 0.3,
    sizeVariance: 0.2,
    life: 0.5,
    lifeVariance: 0.2,
    speed: 2,
    speedVariance: 1,
    gravity: -1,
    spread: 0.3,
    fadeOut: true,
    shrink: true,
    rotationSpeed: 5,
  },
  ice_shards: {
    count: 15,
    color: 0x88ccff,
    colorVariance: 0.1,
    size: 0.2,
    sizeVariance: 0.1,
    life: 0.8,
    lifeVariance: 0.3,
    speed: 3,
    speedVariance: 1,
    gravity: 2,
    spread: 0.5,
    fadeOut: true,
    shrink: false,
    rotationSpeed: 10,
  },
  holy_light: {
    count: 30,
    color: 0xffffaa,
    colorVariance: 0.1,
    size: 0.25,
    sizeVariance: 0.15,
    life: 1.0,
    lifeVariance: 0.3,
    speed: 1.5,
    speedVariance: 0.5,
    gravity: -2,
    spread: 0.8,
    fadeOut: true,
    shrink: true,
    rotationSpeed: 2,
  },
  shadow_tendrils: {
    count: 25,
    color: 0x440066,
    colorVariance: 0.2,
    size: 0.35,
    sizeVariance: 0.15,
    life: 0.7,
    lifeVariance: 0.2,
    speed: 2.5,
    speedVariance: 1,
    gravity: 0,
    spread: 0.6,
    fadeOut: true,
    shrink: true,
    rotationSpeed: 8,
  },
  lightning_spark: {
    count: 40,
    color: 0x8888ff,
    colorVariance: 0.2,
    size: 0.15,
    sizeVariance: 0.1,
    life: 0.2,
    lifeVariance: 0.1,
    speed: 8,
    speedVariance: 4,
    gravity: 0,
    spread: 1.0,
    fadeOut: true,
    shrink: false,
    rotationSpeed: 20,
  },
  arcane_burst: {
    count: 35,
    color: 0xaa44ff,
    colorVariance: 0.2,
    size: 0.25,
    sizeVariance: 0.1,
    life: 0.6,
    lifeVariance: 0.2,
    speed: 4,
    speedVariance: 2,
    gravity: 0,
    spread: 1.2,
    fadeOut: true,
    shrink: true,
    rotationSpeed: 6,
  },
  blood_splash: {
    count: 20,
    color: 0x880000,
    colorVariance: 0.2,
    size: 0.2,
    sizeVariance: 0.1,
    life: 0.4,
    lifeVariance: 0.1,
    speed: 3,
    speedVariance: 1.5,
    gravity: 8,
    spread: 0.6,
    fadeOut: false,
    shrink: false,
    rotationSpeed: 0,
  },
  heal_sparkle: {
    count: 25,
    color: 0x44ff44,
    colorVariance: 0.2,
    size: 0.2,
    sizeVariance: 0.1,
    life: 1.2,
    lifeVariance: 0.4,
    speed: 1,
    speedVariance: 0.5,
    gravity: -1.5,
    spread: 0.5,
    fadeOut: true,
    shrink: false,
    rotationSpeed: 3,
  },
  level_up: {
    count: 100,
    color: 0xffdd00,
    colorVariance: 0.1,
    size: 0.3,
    sizeVariance: 0.2,
    life: 2.0,
    lifeVariance: 0.5,
    speed: 3,
    speedVariance: 1.5,
    gravity: -1,
    spread: 2.0,
    fadeOut: true,
    shrink: false,
    rotationSpeed: 4,
  },
  loot_sparkle: {
    count: 15,
    color: 0xffff00,
    colorVariance: 0.3,
    size: 0.15,
    sizeVariance: 0.1,
    life: 1.5,
    lifeVariance: 0.5,
    speed: 0.5,
    speedVariance: 0.3,
    gravity: -0.5,
    spread: 0.3,
    fadeOut: true,
    shrink: false,
    rotationSpeed: 2,
  },
  death_soul: {
    count: 40,
    color: 0x666688,
    colorVariance: 0.2,
    size: 0.4,
    sizeVariance: 0.2,
    life: 2.0,
    lifeVariance: 0.5,
    speed: 1.5,
    speedVariance: 0.5,
    gravity: -2,
    spread: 1.0,
    fadeOut: true,
    shrink: true,
    rotationSpeed: 1,
  },
  hit_impact: {
    count: 10,
    color: 0xffffff,
    colorVariance: 0.1,
    size: 0.2,
    sizeVariance: 0.1,
    life: 0.2,
    lifeVariance: 0.05,
    speed: 5,
    speedVariance: 2,
    gravity: 0,
    spread: 0.8,
    fadeOut: true,
    shrink: true,
    rotationSpeed: 0,
  },
  critical_hit: {
    count: 25,
    color: 0xff8800,
    colorVariance: 0.2,
    size: 0.3,
    sizeVariance: 0.15,
    life: 0.4,
    lifeVariance: 0.1,
    speed: 6,
    speedVariance: 3,
    gravity: 0,
    spread: 1.0,
    fadeOut: true,
    shrink: true,
    rotationSpeed: 15,
  },
  buff_aura: {
    count: 20,
    color: 0x44aaff,
    colorVariance: 0.1,
    size: 0.15,
    sizeVariance: 0.05,
    life: 0.8,
    lifeVariance: 0.2,
    speed: 0.3,
    speedVariance: 0.1,
    gravity: -0.5,
    spread: 0.5,
    fadeOut: true,
    shrink: false,
    rotationSpeed: 2,
  },
  debuff_aura: {
    count: 20,
    color: 0x884400,
    colorVariance: 0.1,
    size: 0.15,
    sizeVariance: 0.05,
    life: 0.8,
    lifeVariance: 0.2,
    speed: 0.3,
    speedVariance: 0.1,
    gravity: 0.5,
    spread: 0.5,
    fadeOut: true,
    shrink: false,
    rotationSpeed: 2,
  },
};

export class ParticleSystem {
  private scene: THREE.Scene;
  private pools: Map<ParticleEffectType, ParticlePool> = new Map();
  private activeEffects: Set<ParticleEffectType> = new Set();

  // Global particle budget
  private readonly MAX_TOTAL_PARTICLES = 5000;
  private totalActiveParticles = 0;

  // Reusable objects
  private readonly _tempVec = new THREE.Vector3();
  private readonly _tempColor = new THREE.Color();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.initializePools();
  }

  private initializePools(): void {
    for (const [effectType, config] of Object.entries(EFFECT_CONFIGS)) {
      const maxParticles = config.count * 10; // Allow multiple concurrent effects
      this.createPool(effectType as ParticleEffectType, maxParticles);
    }
  }

  private createPool(effectType: ParticleEffectType, maxParticles: number): void {
    const particles: ParticleData[] = [];

    // Pre-allocate particle data
    for (let i = 0; i < maxParticles; i++) {
      particles.push({
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        color: new THREE.Color(),
        size: 0,
        life: 0,
        maxLife: 1,
        gravity: 0,
        fadeOut: true,
        shrink: false,
        rotation: 0,
        rotationSpeed: 0,
      });
    }

    // Create geometry with attributes
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(maxParticles * 3);
    const colors = new Float32Array(maxParticles * 3);
    const sizes = new Float32Array(maxParticles);
    const alphas = new Float32Array(maxParticles);

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('alpha', new THREE.BufferAttribute(alphas, 1));

    // Mark as dynamic
    geometry.attributes.position.usage = THREE.DynamicDrawUsage;
    geometry.attributes.color.usage = THREE.DynamicDrawUsage;
    geometry.attributes.size.usage = THREE.DynamicDrawUsage;
    geometry.attributes.alpha.usage = THREE.DynamicDrawUsage;

    // Create shader material for particles
    const material = new THREE.ShaderMaterial({
      uniforms: {
        pointTexture: { value: this.createParticleTexture() },
      },
      vertexShader: `
        attribute float size;
        attribute float alpha;
        attribute vec3 color;
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          vColor = color;
          vAlpha = alpha;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform sampler2D pointTexture;
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          vec4 texColor = texture2D(pointTexture, gl_PointCoord);
          if (texColor.a < 0.1) discard;
          gl_FragColor = vec4(vColor, vAlpha * texColor.a);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexColors: true,
    });

    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false; // Particles can move outside initial bounds
    this.scene.add(points);

    this.pools.set(effectType, {
      particles,
      activeCount: 0,
      geometry,
      points,
      maxParticles,
    });
  }

  private createParticleTexture(): THREE.Texture {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    // Create radial gradient for soft particle
    const gradient = ctx.createRadialGradient(
      size / 2, size / 2, 0,
      size / 2, size / 2, size / 2
    );
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.8)');
    gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.3)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }

  public emit(
    effectType: ParticleEffectType,
    position: THREE.Vector3,
    direction?: THREE.Vector3,
    scale: number = 1
  ): void {
    const pool = this.pools.get(effectType);
    if (!pool) return;

    const config = EFFECT_CONFIGS[effectType];
    const count = Math.min(
      config.count,
      pool.maxParticles - pool.activeCount,
      this.MAX_TOTAL_PARTICLES - this.totalActiveParticles
    );

    if (count <= 0) return;

    const dir = direction || new THREE.Vector3(0, 1, 0);

    for (let i = 0; i < count; i++) {
      const particle = pool.particles[pool.activeCount + i];

      // Initialize position
      particle.position.copy(position);

      // Random spread direction
      this._tempVec.set(
        (Math.random() - 0.5) * config.spread,
        (Math.random() - 0.5) * config.spread,
        (Math.random() - 0.5) * config.spread
      );
      this._tempVec.add(dir).normalize();

      // Set velocity
      const speed = config.speed + (Math.random() - 0.5) * config.speedVariance * 2;
      particle.velocity.copy(this._tempVec).multiplyScalar(speed * scale);

      // Set color with variance
      particle.color.setHex(config.color);
      if (config.colorVariance > 0) {
        particle.color.r += (Math.random() - 0.5) * config.colorVariance;
        particle.color.g += (Math.random() - 0.5) * config.colorVariance;
        particle.color.b += (Math.random() - 0.5) * config.colorVariance;
      }

      // Set other properties
      particle.size = (config.size + (Math.random() - 0.5) * config.sizeVariance * 2) * scale;
      particle.life = config.life + (Math.random() - 0.5) * config.lifeVariance * 2;
      particle.maxLife = particle.life;
      particle.gravity = config.gravity;
      particle.fadeOut = config.fadeOut;
      particle.shrink = config.shrink;
      particle.rotation = Math.random() * Math.PI * 2;
      particle.rotationSpeed = config.rotationSpeed * (Math.random() - 0.5) * 2;
    }

    pool.activeCount += count;
    this.totalActiveParticles += count;
    this.activeEffects.add(effectType);
  }

  public update(deltaTime: number): void {
    for (const effectType of this.activeEffects) {
      const pool = this.pools.get(effectType);
      if (!pool || pool.activeCount === 0) {
        this.activeEffects.delete(effectType);
        continue;
      }

      const positions = pool.geometry.attributes.position.array as Float32Array;
      const colors = pool.geometry.attributes.color.array as Float32Array;
      const sizes = pool.geometry.attributes.size.array as Float32Array;
      const alphas = pool.geometry.attributes.alpha.array as Float32Array;

      let writeIndex = 0;

      for (let i = 0; i < pool.activeCount; i++) {
        const particle = pool.particles[i];

        // Update life
        particle.life -= deltaTime;

        if (particle.life <= 0) {
          this.totalActiveParticles--;
          continue;
        }

        // Update physics
        particle.velocity.y -= particle.gravity * deltaTime;
        particle.position.add(this._tempVec.copy(particle.velocity).multiplyScalar(deltaTime));
        particle.rotation += particle.rotationSpeed * deltaTime;

        // Calculate life ratio
        const lifeRatio = particle.life / particle.maxLife;

        // Update buffer attributes
        const i3 = writeIndex * 3;
        positions[i3] = particle.position.x;
        positions[i3 + 1] = particle.position.y;
        positions[i3 + 2] = particle.position.z;

        colors[i3] = particle.color.r;
        colors[i3 + 1] = particle.color.g;
        colors[i3 + 2] = particle.color.b;

        sizes[writeIndex] = particle.shrink ? particle.size * lifeRatio : particle.size;
        alphas[writeIndex] = particle.fadeOut ? lifeRatio : 1;

        // Compact active particles
        if (writeIndex !== i) {
          pool.particles[writeIndex] = particle;
        }
        writeIndex++;
      }

      pool.activeCount = writeIndex;

      // Update geometry
      pool.geometry.attributes.position.needsUpdate = true;
      pool.geometry.attributes.color.needsUpdate = true;
      pool.geometry.attributes.size.needsUpdate = true;
      pool.geometry.attributes.alpha.needsUpdate = true;
      pool.geometry.setDrawRange(0, pool.activeCount);

      if (pool.activeCount === 0) {
        this.activeEffects.delete(effectType);
      }
    }
  }

  // Continuous effect emitters for auras, trails, etc.
  public emitContinuous(
    effectType: ParticleEffectType,
    position: THREE.Vector3,
    ratePerSecond: number,
    deltaTime: number
  ): void {
    const particlesToEmit = Math.floor(ratePerSecond * deltaTime);
    for (let i = 0; i < particlesToEmit; i++) {
      this.emit(effectType, position, undefined, 0.5);
    }

    // Handle fractional particles
    if (Math.random() < (ratePerSecond * deltaTime) % 1) {
      this.emit(effectType, position, undefined, 0.5);
    }
  }

  // Trail effect that follows a moving object
  public emitTrail(
    effectType: ParticleEffectType,
    startPos: THREE.Vector3,
    endPos: THREE.Vector3,
    segments: number = 5
  ): void {
    for (let i = 0; i < segments; i++) {
      const t = i / (segments - 1);
      this._tempVec.lerpVectors(startPos, endPos, t);
      this.emit(effectType, this._tempVec, undefined, 0.3);
    }
  }

  public getActiveParticleCount(): number {
    return this.totalActiveParticles;
  }

  public destroy(): void {
    for (const pool of this.pools.values()) {
      pool.geometry.dispose();
      (pool.points.material as THREE.Material).dispose();
      this.scene.remove(pool.points);
    }
    this.pools.clear();
    this.activeEffects.clear();
  }
}
